import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import SearchBar from '../components/SearchBar';
import Breadcrumb from '../components/Breadcrumb';
import { getDistricts } from '../services/locationService';
import { createSlug, formatTitle } from '../utils/url';

export default function Districts() {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  const [districts, setDistricts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (citySlug) {
      loadDistricts(citySlug);
    }
  }, [citySlug]);

  const loadDistricts = async (citySlug: string) => {
    try {
      setLoading(true);
      const data = await getDistricts(citySlug);
      setDistricts(data);
    } catch (err) {
      console.error('Error loading districts:', err);
      setError('İlçeler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleDistrictClick = (districtName: string) => {
    window.scrollTo(0, 0);
    const cityName = districts[0]?.city?.name;
    navigate(`/ilce/${createSlug(cityName)}/${createSlug(districtName)}`);
  };

  const filteredDistricts = districts.filter(district =>
    district.name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-[#24272e] rounded-lg w-1/4"></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="h-20 bg-[#24272e] rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const cityName = formatTitle(districts[0]?.city?.name || citySlug?.replace(/-/g, ' ') || '');

  return (
    <div className="container mx-auto px-4">
      <div className="max-w-6xl mx-auto">
        <Breadcrumb items={[
          { name: cityName, path: `/il/${createSlug(cityName)}` }
        ]} />
        
        <div className="bg-[#24272e] rounded-3xl border border-white/10 shadow-2xl overflow-hidden mt-6">
          <div className="p-8 md:p-12">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-2xl shadow-lg shadow-yellow-400/20">
                  <Building2 className="w-8 h-8 text-black" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-white">
                    {cityName} İlçeleri
                  </h1>
                  <p className="text-white/60">Size en yakın taksi durağını bulun</p>
                </div>
              </div>
              <SearchBar 
                placeholder="İlçe ara..."
                onChange={setSearch}
              />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filteredDistricts.map(district => (
                <button
                  key={district.id}
                  onClick={() => handleDistrictClick(district.name)}
                  className="group relative flex items-center justify-center p-6 bg-[#1a1d24] hover:bg-[#2d3039] rounded-xl transition-all duration-300 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/0 to-yellow-400/0 group-hover:from-yellow-400/5 group-hover:to-yellow-400/0 transition-all duration-300" />
                  <div className="relative text-center">
                    <p className="text-lg font-medium text-white group-hover:text-yellow-400 transition-colors">
                      {district.name}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            {filteredDistricts.length === 0 && (
              <div className="text-center py-12">
                <p className="text-white/60">Aradığınız kriterlere uygun ilçe bulunamadı.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}